import "./LandingPage.css";
import "./LandingRefinements.css";
import { useEffect, useRef, useState } from "react";
import Lenis from "lenis";
import { motion, useScroll, useSpring } from "framer-motion";
import {
  ArrowRight,
  ArrowUp,
  ArrowUpRight,
  CheckCircle2,
  Clock3,
  Home,
  Instagram,
  Mail,
  MapPin,
  Menu,
  MessageCircle,
  ShieldCheck,
  Stethoscope,
  UserCheck,
  Video,
  X,
} from "lucide-react";
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "@/components/ui/accordion";
import { ConsultationIntake } from "@/components/ConsultationIntake";

const heroImage = "https://images.unsplash.com/photo-1706353399656-210cca727a33?crop=entropy&cs=srgb&fm=jpg&ixid=M3w7NDk1ODF8MHwxfHNlYXJjaHwxfHxwaHlzaW90aGVyYXBpc3QlMjBzcGluZSUyMGFzc2Vzc21lbnQlMjBwb3N0dXJlJTIwY2xpbmljfGVufDB8fHx8MTc4NzgxMTgxMHww&ixlib=rb-4.1.0&q=85";
const methodImage = "https://images.unsplash.com/photo-1624716346720-6c96dfd07807?crop=entropy&cs=srgb&fm=jpg&ixid=M3w7NDk1ODF8MHwxfHNlYXJjaHwzfHxwaHlzaW90aGVyYXBpc3QlMjBzcGluZSUyMGFzc2Vzc21lbnQlMjBwb3N0dXJlJTIwY2xpbmljfGVufDB8fHx8MTc4NzgxMTgxMHww&ixlib=rb-4.1.0&q=85";
const careImage = "https://images.unsplash.com/photo-1699523229199-fce5aa6b0ec3?crop=entropy&cs=srgb&fm=jpg&ixid=M3w7NDk1ODF8MHwxfHNlYXJjaHw0fHxwaHlzaW90aGVyYXBpc3QlMjBzcGluZSUyMGFzc2Vzc21lbnQlMjBwb3N0dXJlJTIwY2xpbmljfGVufDB8fHx8MTc4NzgxMTgxMHww&ixlib=rb-4.1.0&q=85";

const whatsappBaseUrl = "https://wa.me/923038444433";
const bookingUrl = `${whatsappBaseUrl}?text=Assalam-o-Alaikum%20Zuhab%2C%20I%20would%20like%20to%20book%20a%20physiotherapy%20consultation`;
const emailUrl = "mailto:Zuhab@fixmyposture.pk?subject=Consultation%20request";
const clinicAddress = "117, Block B Gulberg 2, Lahore, Pakistan";
const clinicDirectionsUrl = "https://www.google.com/maps/search/?api=1&query=117%20Block%20B%20Gulberg%202%20Lahore%20Pakistan";

const navItems = [
  ["Problem", "problem"],
  ["Solution", "solution"],
  ["Benefits", "benefits"],
  ["Proof", "proof"],
  ["Process", "process"],
  ["Intake", "intake"],
  ["FAQ", "faq"],
];

const painPoints = [
  ["Rounded shoulders", "From long hours at a desk or looking down at a screen."],
  ["Stiff neck by evening", "The quiet build-up of commuting, sitting, and limited movement."],
  ["Lower back flare-ups", "Pain that appears the moment you stand after sitting too long."],
];

const features = [
  {
    icon: Stethoscope,
    title: "Physiotherapist-Led, Not Guesswork",
    text: "Every recommendation comes from real clinical training—not repurposed fitness content or a one-size-fits-all routine.",
  },
  {
    icon: Video,
    title: "In-Clinic or Online—Your Choice",
    text: "Choose a hands-on in-person assessment or get the same quality of evaluation through a video consultation.",
  },
  {
    icon: UserCheck,
    title: "Personalized, Not Generic",
    text: "Your plan is built around your posture, habits, symptoms, and daily routine—not a template made for everyone.",
  },
  {
    icon: Clock3,
    title: "Built for Real, Everyday Life",
    text: "Exercises designed around long commutes, desk work, and standing all day—not a gym-only routine you cannot sustain.",
  },
  {
    icon: Home,
    title: "Simple, No-Equipment Movements",
    text: "Most exercises can be done at home or at your desk, so consistency does not depend on special equipment.",
  },
];

const proofMetrics = [
  { value: "7,100+", label: "People reached" },
  { value: "11,300+", label: "Educational views" },
  { value: "93.6%", label: "Organic discovery" },
  { value: "63", label: "Shares on one video" },
];

const steps = [
  {
    title: "Book Your Consultation",
    text: "Choose in-clinic or online—whichever fits your situation.",
  },
  {
    title: "Get Assessed",
    text: "A real evaluation identifies the actual cause of your pain, not just the symptoms.",
  },
  {
    title: "Receive Your Personalized Plan",
    text: "Simple, targeted exercises built around your body and daily routine using The FIX Method.",
  },
  {
    title: "Fix It, Don’t Just Manage It",
    text: "Follow your plan with ongoing guidance so the pain is addressed, not temporarily masked.",
  },
];

const faqs = [
  {
    question: "Do I have to visit in person, or can I do this online?",
    answer: "Both options are available. In-clinic consultations offer a hands-on assessment, while online consultations provide the same quality of evaluation and a personalized plan over video call, wherever you are.",
  },
  {
    question: "How long before I notice a difference?",
    answer: "It varies by individual, but many people notice initial improvement within a few weeks of consistently following their personalized plan.",
  },
  {
    question: "Do I need any special equipment?",
    answer: "No. Most exercises are bodyweight-based and can be done at home or even at your desk.",
  },
  {
    question: "Is this safe if I already have a diagnosed injury or chronic condition?",
    answer: "In most cases, yes. Mention any existing diagnosis during your consultation so your plan can be adjusted safely around it.",
  },
  {
    question: "How is this different from following workout videos online?",
    answer: "Generic videos are not built for your body or the actual cause of your pain. This starts with a real physiotherapist’s assessment, so your plan is built for you—not a general audience.",
  },
];

const ease = [0.22, 1, 0.36, 1];
const COUNTDOWN_KEY = "fixmyposture_consultation_deadline";

function formatCountdownUnit(value) {
  return String(value).padStart(2, "0");
}

function LandingReveal({ children, className = "", delay = 0 }) {
  return (
    <motion.div
      className={className}
      initial={{ opacity: 0, y: 34, filter: "blur(8px)" }}
      whileInView={{ opacity: 1, y: 0, filter: "blur(0px)" }}
      viewport={{ once: true, amount: 0.18 }}
      transition={{ duration: 0.72, delay, ease }}
    >
      {children}
    </motion.div>
  );
}

function LandingButton({ children, href = bookingUrl, variant = "primary", testId }) {
  return (
    <motion.a
      className={`landing-button landing-button-${variant}`}
      href={href}
      target={href.startsWith("http") ? "_blank" : undefined}
      rel={href.startsWith("http") ? "noreferrer" : undefined}
      whileHover={{ y: -3 }}
      whileTap={{ scale: 0.97 }}
      data-testid={testId}
    >
      {children}
    </motion.a>
  );
}

function SectionIntro({ kicker, title, text, dark = false }) {
  return (
    <LandingReveal className={`landing-section-intro ${dark ? "is-dark" : ""}`}>
      <p className="landing-kicker">{kicker}</p>
      <h2>{title}</h2>
      {text && <p className="landing-section-text">{text}</p>}
    </LandingReveal>
  );
}

export default function LandingPage() {
  const [menuOpen, setMenuOpen] = useState(false);
  const [showStickyCta, setShowStickyCta] = useState(false);
  const [timeLeft, setTimeLeft] = useState({ hours: 24, minutes: 0, seconds: 0 });
  const lenisRef = useRef(null);
  const { scrollYProgress } = useScroll();
  const progress = useSpring(scrollYProgress, { stiffness: 120, damping: 28, mass: 0.35 });

  useEffect(() => {
    const dayInMs = 24 * 60 * 60 * 1000;
    let deadline = Number(window.localStorage.getItem(COUNTDOWN_KEY));
    if (!deadline || deadline <= Date.now()) {
      deadline = Date.now() + dayInMs;
      window.localStorage.setItem(COUNTDOWN_KEY, String(deadline));
    }

    const updateCountdown = () => {
      let remaining = deadline - Date.now();
      if (remaining <= 0) {
        deadline = Date.now() + dayInMs;
        window.localStorage.setItem(COUNTDOWN_KEY, String(deadline));
        remaining = dayInMs;
      }
      setTimeLeft({
        hours: Math.floor(remaining / 3_600_000),
        minutes: Math.floor((remaining % 3_600_000) / 60_000),
        seconds: Math.floor((remaining % 60_000) / 1000),
      });
    };

    updateCountdown();
    const interval = window.setInterval(updateCountdown, 1000);
    return () => window.clearInterval(interval);
  }, []);

  useEffect(() => {
    const updateStickyCta = () => setShowStickyCta(window.scrollY > window.innerHeight * 0.68);
    updateStickyCta();
    window.addEventListener("scroll", updateStickyCta, { passive: true });
    return () => window.removeEventListener("scroll", updateStickyCta);
  }, []);

  useEffect(() => {
    const lenis = new Lenis({ duration: 1.08, smoothWheel: true, wheelMultiplier: 0.92 });
    lenisRef.current = lenis;
    let frame;
    const raf = (time) => {
      lenis.raf(time);
      frame = requestAnimationFrame(raf);
    };
    frame = requestAnimationFrame(raf);
    return () => {
      cancelAnimationFrame(frame);
      lenis.destroy();
    };
  }, []);

  const scrollTo = (id) => {
    const target = document.getElementById(id);
    if (!target) return;
    lenisRef.current?.scrollTo(target, { offset: -82, duration: 1.05 });
    setMenuOpen(false);
  };

  return (
    <div className="landing-shell">
      <motion.div className="landing-scroll-progress" style={{ scaleX: progress }} />
      <motion.header className="landing-header" initial={{ y: -80 }} animate={{ y: 0 }} transition={{ duration: 0.65, ease }} data-testid="site-navigation">
        <div className="landing-header-inner">
          <button className="landing-brand" onClick={() => scrollTo("top")} data-testid="landing-brand-home">
            <span>F</span>
            <strong>Fix My Posture</strong>
          </button>
          <nav className="landing-nav" aria-label="Primary navigation" data-testid="landing-desktop-nav">
            {navItems.map(([label, id]) => <button key={id} onClick={() => scrollTo(id)} data-testid={`landing-nav-${id}`}>{label}</button>)}
          </nav>
          <div className="landing-header-actions">
            <a className="landing-header-cta" href={bookingUrl} target="_blank" rel="noreferrer" data-testid="header-booking-button">
              Book now <ArrowUpRight size={15} />
            </a>
            <button className="landing-menu-toggle" onClick={() => setMenuOpen((open) => !open)} aria-label="Toggle menu" data-testid="landing-mobile-menu-button">
              {menuOpen ? <X size={23} /> : <Menu size={23} />}
            </button>
          </div>
        </div>
        {menuOpen && (
          <motion.div className="landing-mobile-menu" initial={{ opacity: 0, y: -14 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.24 }} data-testid="landing-mobile-menu">
            {navItems.map(([label, id]) => <button key={id} onClick={() => scrollTo(id)} data-testid={`landing-mobile-nav-${id}`}>{label}</button>)}
            <a href={bookingUrl} target="_blank" rel="noreferrer" data-testid="mobile-booking-button">Book Your Consultation</a>
          </motion.div>
        )}
      </motion.header>

      <main id="top">
        <section className="landing-hero" data-testid="hero-section">
          <div className="landing-hero-inner">
            <div className="landing-hero-copy">
              <motion.div className="landing-hero-badge" initial={{ opacity: 0, y: 18 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.65, ease }} data-testid="hero-trust-badge">
                <ShieldCheck size={15} /> Physiotherapist-led care · In clinic + online
              </motion.div>
              <motion.h1 initial={{ opacity: 0, y: 34 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.82, delay: 0.1, ease }}>
                Stop Managing the Pain. <span>Start Fixing It.</span>
              </motion.h1>
              <motion.p className="landing-hero-subtitle" initial={{ opacity: 0, y: 26 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.75, delay: 0.24, ease }}>
                Physiotherapist-led relief for back, neck, and posture pain—in-clinic or online. Real assessments and personalized fixes, not generic exercise videos.
              </motion.p>
              <motion.div className="landing-hero-actions" initial={{ opacity: 0, y: 24 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.75, delay: 0.36, ease }}>
                <LandingButton testId="hero-booking-button">Book Your Consultation <ArrowRight size={17} /></LandingButton>
                <button className="landing-text-button" onClick={() => scrollTo("process")} data-testid="hero-process-button">See how it works <ArrowRight size={16} /></button>
              </motion.div>
              <motion.div className="landing-countdown" initial={{ opacity: 0, y: 22 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.7, delay: 0.46, ease }} data-testid="hero-countdown-timer" aria-label="Next consultation slots refresh in 24 hours">
                <span className="landing-countdown-label">Next consultation slots refresh in</span>
                <div className="landing-countdown-units">
                  <div className="landing-countdown-unit" data-testid="countdown-hours"><strong>{formatCountdownUnit(timeLeft.hours)}</strong><span>Hours</span></div>
                  <div className="landing-countdown-unit" data-testid="countdown-minutes"><strong>{formatCountdownUnit(timeLeft.minutes)}</strong><span>Minutes</span></div>
                  <div className="landing-countdown-unit" data-testid="countdown-seconds"><strong>{formatCountdownUnit(timeLeft.seconds)}</strong><span>Seconds</span></div>
                </div>
              </motion.div>
              <motion.div className="landing-hero-proof" initial={{ opacity: 0, y: 24 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.75, delay: 0.56, ease }} data-testid="hero-proof-strip">
                <div><strong>10+ years</strong><span>Clinical practice</span></div>
                <div><strong>7.1K+</strong><span>People reached</span></div>
                <div><strong>93.6%</strong><span>Organic discovery</span></div>
              </motion.div>
            </div>
            <motion.div className="landing-hero-visual" initial={{ opacity: 0, y: 56, rotate: 2 }} animate={{ opacity: 1, y: 0, rotate: 0 }} transition={{ duration: 0.95, delay: 0.22, ease }} data-testid="hero-visual">
              <div className="landing-image-frame">
                <img src={heroImage} alt="Physiotherapist hands-on spinal examination in clinic" />
                <div className="landing-image-label"><span /> Real assessment. Real plan.</div>
              </div>
              <motion.div className="landing-fix-card" animate={{ y: [0, -10, 0] }} transition={{ duration: 4.8, repeat: Infinity, ease: "easeInOut" }}>
                <span>The FIX Method</span>
                <strong>Assess · Target · Sustain</strong>
              </motion.div>
              <motion.div className="landing-care-chip" animate={{ y: [0, 9, 0] }} transition={{ duration: 5.4, repeat: Infinity, ease: "easeInOut", delay: 0.4 }}>
                <Video size={15} /> Online consultations available
              </motion.div>
            </motion.div>
          </div>
        </section>

        <section id="problem" className="landing-section landing-problem-section" data-testid="problem-agitation-section">
          <div className="landing-container">
            <LandingReveal className="landing-problem-panel">
              <div className="landing-problem-copy">
                <p className="landing-kicker">The real problem</p>
                <h2>Your body is keeping score of modern life.</h2>
                <p>If you sit at a desk, commute long distances, or spend hours on your feet, your body is paying for it quietly, every single day.</p>
                <p>Most people try random internet exercises without knowing whether they address the real cause. Some feel temporary relief; others are unsure whether what they feel is normal or needs attention.</p>
                <div className="landing-warning" data-testid="problem-warning"><strong>Generic advice treats the symptom, not the source.</strong><span>Without knowing the cause, the same pain keeps coming back.</span></div>
              </div>
              <div className="landing-pain-grid">
                {painPoints.map(([title, text], index) => (
                  <motion.article key={title} initial={{ opacity: 0, y: 24 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ duration: 0.55, delay: index * 0.08, ease }} data-testid={`pain-point-${index + 1}`}>
                    <span>0{index + 1}</span>
                    <h3>{title}</h3>
                    <p>{text}</p>
                  </motion.article>
                ))}
              </div>
            </LandingReveal>
          </div>
        </section>

        <section id="solution" className="landing-section landing-solution-section" data-testid="solution-section">
          <div className="landing-container landing-solution-grid">
            <div>
              <SectionIntro
                kicker="The solution"
                title="A structured method for finding the cause—and fixing it."
                text="I’m Zuhab, a physiotherapist with a decade of hands-on experience in physiotherapy and personal training. Instead of generic routines, I use The FIX Method to build a simple plan around your body and your daily life."
              />
              <div className="landing-method-list">
                {["Identify the individual factors contributing to pain", "Select targeted, practical exercises", "Build a routine you can sustain"].map((item, index) => (
                  <LandingReveal key={item} className="landing-method-item" delay={index * 0.07}>
                    <span>0{index + 1}</span>
                    <p>{item}</p>
                  </LandingReveal>
                ))}
              </div>
            </div>
            <LandingReveal className="landing-solution-visual" delay={0.14}>
              <img src={methodImage} alt="Anatomical spine model used in patient education" />
              <div className="landing-method-overlay">
                <span>The FIX Method</span>
                <strong>Assessment before advice.</strong>
              </div>
            </LandingReveal>
          </div>
          <div className="landing-container landing-care-options">
            <LandingReveal className="landing-care-card" delay={0.05}>
              <MapPin size={24} />
              <h3>In-Clinic Consultation</h3>
              <p>Prefer a hands-on, in-person assessment? Visit the clinic at {clinicAddress}.</p>
              <a className="landing-care-link" href={clinicDirectionsUrl} target="_blank" rel="noreferrer" data-testid="care-directions-link">Get directions <ArrowUpRight size={15} /></a>
            </LandingReveal>
            <LandingReveal className="landing-care-card is-online" delay={0.12}>
              <Video size={24} />
              <h3>Online Consultation</h3>
              <p>Based elsewhere or unable to visit? Get the same quality of evaluation and personalized plan over video call.</p>
            </LandingReveal>
          </div>
        </section>

        <section id="benefits" className="landing-section landing-features-section" data-testid="feature-cards-section">
          <div className="landing-container">
            <SectionIntro
              kicker="Features & benefits"
              title="Care designed around your body—not generic content."
              text="Every part of the process is built to move you from confusion and temporary relief toward a clear, sustainable plan."
            />
            <div className="landing-features-grid">
              {features.map((feature, index) => {
                const Icon = feature.icon;
                return (
                  <motion.article
                    className="landing-feature-card"
                    key={feature.title}
                    initial={{ opacity: 0, y: 30 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    viewport={{ once: true, amount: 0.18 }}
                    transition={{ duration: 0.62, delay: index * 0.06, ease }}
                    whileHover={{ y: -7 }}
                    data-testid={`feature-card-${index + 1}`}
                  >
                    <div className="landing-feature-icon"><Icon size={23} /></div>
                    <h3>{feature.title}</h3>
                    <p>{feature.text}</p>
                  </motion.article>
                );
              })}
            </div>
          </div>
        </section>

        <section id="proof" className="landing-section landing-proof-section" data-testid="social-proof-section">
          <div className="landing-container">
            <SectionIntro
              kicker="Social proof"
              title="Physiotherapist-led advice people trust enough to share."
              text="Since launching, @fixmyposture.pk has reached thousands of people with practical, clinically grounded education."
              dark
            />
            <div className="landing-proof-grid">
              {proofMetrics.map((metric, index) => (
                <motion.div className="landing-proof-metric" key={metric.label} initial={{ opacity: 0, y: 24 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ duration: 0.55, delay: index * 0.07, ease }} data-testid={`proof-metric-${index + 1}`}>
                  <strong>{metric.value}</strong>
                  <span>{metric.label}</span>
                </motion.div>
              ))}
            </div>
            <div className="landing-testimonial-grid">
              <LandingReveal className="landing-testimonial" delay={0.08}>
                <p>“A single video brought in 19 new followers because it addressed a clear, relatable pain problem.”</p>
                <span>Audience response · @fixmyposture.pk</span>
              </LandingReveal>
              <LandingReveal className="landing-testimonial" delay={0.16}>
                <p>“One video was shared 63 times—a clear sign people find physiotherapist-led advice useful enough to pass along.”</p>
                <span>Organic discovery · 93.6% non-follower reach</span>
              </LandingReveal>
            </div>
            <LandingReveal className="landing-testimonial-note" delay={0.2}>
              Client testimonials will be added here as real feedback comes in from in-clinic and online consultations.
            </LandingReveal>
          </div>
        </section>

        <section id="process" className="landing-section landing-process-section" data-testid="how-it-works-section">
          <div className="landing-container">
            <SectionIntro
              kicker="How it works"
              title="Four steps from pain management to real progress."
              text="A clear process replaces guesswork with assessment, personalization, and guided consistency."
            />
            <div className="landing-process-grid">
              {steps.map((step, index) => (
                <motion.article className="landing-process-card" key={step.title} initial={{ opacity: 0, y: 32 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true, amount: 0.2 }} transition={{ duration: 0.62, delay: index * 0.08, ease }} data-testid={`process-step-${index + 1}`}>
                  <span>Step {index + 1}</span>
                  <h3>{step.title}</h3>
                  <p>{step.text}</p>
                  {index < steps.length - 1 && <ArrowRight className="landing-process-arrow" size={22} />}
                </motion.article>
              ))}
            </div>
            <LandingReveal className="landing-process-image" delay={0.16}>
              <img src={careImage} alt="Physiotherapist performing gentle spinal palpation" />
              <div><CheckCircle2 size={20} /> Personalized plan built around your routine</div>
            </LandingReveal>
          </div>
        </section>

        <ConsultationIntake whatsappBaseUrl={whatsappBaseUrl} clinicAddress={clinicAddress} directionsUrl={clinicDirectionsUrl} />

        <section id="faq" className="landing-section landing-faq-section" data-testid="faq-section">
          <div className="landing-container landing-faq-grid">
            <SectionIntro
              kicker="FAQ"
              title="Questions people ask before booking."
              text="Clear answers about online care, safety, equipment, and how this differs from generic exercise videos."
            />
            <LandingReveal className="landing-faq-list" delay={0.1}>
              <Accordion type="single" collapsible defaultValue="item-0">
                {faqs.map((faq, index) => (
                  <AccordionItem value={`item-${index}`} key={faq.question} className="landing-faq-item" data-testid={`faq-item-${index + 1}`}>
                    <AccordionTrigger className="landing-faq-trigger" data-testid={`faq-trigger-${index + 1}`}>{faq.question}</AccordionTrigger>
                    <AccordionContent className="landing-faq-content" data-testid={`faq-content-${index + 1}`}>{faq.answer}</AccordionContent>
                  </AccordionItem>
                ))}
              </Accordion>
            </LandingReveal>
          </div>
        </section>

        <section id="book" className="landing-section landing-final-section" data-testid="final-cta-section">
          <div className="landing-container">
            <LandingReveal className="landing-final-panel">
              <p className="landing-kicker">Ready when you are</p>
              <h2>Pain doesn’t fix itself. Start before it gets worse.</h2>
              <p>The longer poor posture patterns continue, the more they set in—and the harder they become to reverse. Book an in-clinic or online consultation and get a plan built for your body.</p>
              <div className="landing-final-actions">
                <LandingButton testId="final-booking-button">Book Your Consultation Today <MessageCircle size={17} /></LandingButton>
                <LandingButton href={emailUrl} variant="secondary" testId="final-email-button">Email instead <Mail size={17} /></LandingButton>
              </div>
              <span className="landing-booking-note" data-testid="booking-contact-note">WhatsApp booking opens directly in a new tab. You can also email Zuhab at Zuhab@fixmyposture.pk.</span>
            </LandingReveal>
          </div>
        </section>
      </main>

      {showStickyCta && !menuOpen && (
        <motion.a
          className="landing-sticky-cta"
          href={bookingUrl}
          target="_blank"
          rel="noreferrer"
          initial={{ opacity: 0, y: 72 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.32, ease }}
          data-testid="sticky-mobile-booking-button"
        >
          <MessageCircle size={19} />
          <span>Book Consultation</span>
        </motion.a>
      )}

      <footer className="landing-footer" data-testid="site-footer">
        <div className="landing-footer-grid">
          <div>
            <div className="landing-footer-brand"><span>F</span><strong>Fix My Posture</strong></div>
            <p>Physiotherapist-led relief for back, neck, and posture pain—in clinic and online.</p>
          </div>
          <div>
            <h3>Navigate</h3>
            {navItems.slice(0, 4).map(([label, id]) => <button key={id} onClick={() => scrollTo(id)} data-testid={`footer-nav-${id}`}>{label}</button>)}
          </div>
          <div>
            <h3>Contact</h3>
            <a href={bookingUrl} target="_blank" rel="noreferrer" data-testid="footer-whatsapp-link">+92 303 8444433</a>
            <a href={emailUrl} data-testid="footer-email-link">Zuhab@fixmyposture.pk</a>
            <a href={clinicDirectionsUrl} target="_blank" rel="noreferrer" data-testid="footer-directions-link">{clinicAddress}</a>
            <a href="https://instagram.com/fixmyposture.pk" target="_blank" rel="noreferrer" data-testid="footer-instagram-link">@fixmyposture.pk</a>
          </div>
          <div>
            <h3>Important</h3>
            <p>Educational content does not replace an individual medical assessment. Existing diagnoses should be discussed during consultation.</p>
            <button className="landing-back-top" onClick={() => scrollTo("top")} data-testid="footer-back-to-top">Back to top <ArrowUp size={15} /></button>
          </div>
        </div>
        <div className="landing-footer-bottom">
          <span>© {new Date().getFullYear()} Zuhab Physiotherapy · @fixmyposture.pk</span>
          <div><Instagram size={16} /><Mail size={16} /></div>
        </div>
      </footer>
    </div>
  );
}
